import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

export default function BrandingEditor() {
    const { muniId } = useParams<{ muniId: string }>();
    const queryClient = useQueryClient();
    // const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');

    // Fetch Branding
    const { data: branding, isLoading } = useQuery({
        queryKey: ['branding', muniId],
        queryFn: async () => {
            const res = await fetch(`${API_URL}/municipalities/${muniId}/branding`);
            if (!res.ok) throw new Error('Failed to fetch branding');
            return res.json();
        }
    });

    // Form State
    const [formData, setFormData] = useState({
        primary_color: '#1976d2',
        secondary_color: '#1565c0',
        tagline: '',
        description: '',
        phone: '',
        email: '',
        address: ''
    });

    useEffect(() => {
        if (branding) {
            setFormData({
                primary_color: branding.primary_color || '#1976d2',
                secondary_color: branding.secondary_color || '#1565c0',
                tagline: branding.tagline || '',
                description: branding.description || '',
                phone: branding.phone || '',
                email: branding.email || '',
                address: branding.address || ''
            });
        }
    }, [branding]);

    // Update Mutation
    const updateMutation = useMutation({
        mutationFn: async (data: any) => {
            const res = await fetch(`${API_URL}/municipalities/${muniId}/branding`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (!res.ok) throw new Error('Update failed');
            return res.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['branding', muniId] });
            alert('Branding actualizado correctamente ✅');
        },
        onError: () => alert('Error al actualizar ❌')
    });

    // Upload Handler
    const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>, type: string) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', type);

        try {
            const res = await fetch(`${API_URL}/municipalities/${muniId}/upload`, {
                method: 'POST',
                body: formData
            });
            if (!res.ok) throw new Error('Upload failed');

            const { url } = await res.json();

            // Assume the API returns the key, we need to save this URL in the branding table
            // Ideally backend handles this association, but for now we might need to verify logic.
            // For now, let's assume successful upload means we can update the UI or DB.
            // Actually, best practice: Upload -> Get URL -> Update Branding with new URL
            // Let's do that:

            await updateMutation.mutateAsync({
                [`${type}_url`]: url // Assuming standard naming: logo_url, banner_url
            });

        } catch (err) {
            console.error(err);
            alert('Error al subir imagen');
        }
    };

    if (isLoading) return <div>Cargando...</div>;

    return (
        <div style={{ padding: '20px', display: 'flex', gap: '20px', height: '100vh' }}>
            {/* Editor Panel */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px', borderRight: '1px solid #ccc' }}>
                <h2>🎨 Editor de Identidad</h2>

                <div style={{ marginBottom: '20px' }}>
                    <h3>Colores</h3>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <label>
                            Primario:
                            <input
                                type="color"
                                value={formData.primary_color}
                                onChange={e => setFormData({ ...formData, primary_color: e.target.value })}
                            />
                        </label>
                        <label>
                            Secundario:
                            <input
                                type="color"
                                value={formData.secondary_color}
                                onChange={e => setFormData({ ...formData, secondary_color: e.target.value })}
                            />
                        </label>
                    </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <h3>Textos</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <input
                            placeholder="Slogan (Tagline)"
                            value={formData.tagline}
                            onChange={e => setFormData({ ...formData, tagline: e.target.value })}
                        />
                        <textarea
                            placeholder="Descripción"
                            value={formData.description}
                            onChange={e => setFormData({ ...formData, description: e.target.value })}
                        />
                    </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <h3>Contacto</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <input
                            placeholder="Teléfono"
                            value={formData.phone}
                            onChange={e => setFormData({ ...formData, phone: e.target.value })}
                        />
                        <input
                            placeholder="Email"
                            value={formData.email}
                            onChange={e => setFormData({ ...formData, email: e.target.value })}
                        />
                        <input
                            placeholder="Dirección"
                            value={formData.address}
                            onChange={e => setFormData({ ...formData, address: e.target.value })}
                        />
                    </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <h3>Imágenes</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <label>
                            Logo:
                            <input type="file" onChange={e => handleUpload(e, 'logo')} />
                        </label>
                        <label>
                            Banner:
                            <input type="file" onChange={e => handleUpload(e, 'banner')} />
                        </label>
                    </div>
                </div>

                <button
                    onClick={() => updateMutation.mutate(formData)}
                    style={{ padding: '10px 20px', background: 'blue', color: 'white' }}
                >
                    Guardar Cambios 💾
                </button>
            </div>

            {/* Live Preview */}
            <div style={{ flex: 1, background: '#f5f5f5', padding: '20px', display: 'flex', flexDirection: 'column' }}>
                <h3>Vista Previa</h3>
                <div style={{
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    height: '800px',
                    backgroundColor: 'white',
                    position: 'relative'
                }}>
                    {/* Mock Landing Page */}
                    <div style={{
                        '--primary': formData.primary_color,
                        '--primary-dark': formData.secondary_color
                    } as any}>
                        <div style={{
                            background: `linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%)`,
                            color: 'white',
                            padding: '40px 20px',
                            textAlign: 'center'
                        }}>
                            {branding?.logo_url && <img src={branding.logo_url} style={{ height: '80px', marginBottom: '10px' }} />}
                            <h1>{branding?.municipality?.name || 'Municipio'}</h1>
                            <p>{formData.tagline || 'Tu municipio inteligente'}</p>
                        </div>
                        <div style={{ padding: '20px' }}>
                            <h4>Contacto</h4>
                            <p>📞 {formData.phone}</p>
                            <p>✉️ {formData.email}</p>
                            <p>📍 {formData.address}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
